import { ref } from "vue"
import type { RouteLocationNormalized } from "vue-router"
import { api } from "./api"
import { peekQuery, qk, warmQuery } from "./querycache"

/** Where a navigation should leave the viewport.
 *
 *  Lives here rather than in router.ts because router.ts calls createWebHistory()
 *  at module scope, which needs a DOM — and this policy is the one part of the
 *  scroll fix a unit test can pin.
 *
 *  vue-router calls this ONE MICROTASK after the route commits (handleScroll =
 *  `nextTick().then(() => scrollBehavior(...))`) and awaits the result. That used
 *  to run while the OUTGOING page was still mounted, because <Transition
 *  mode="out-in"> delayed creating the incoming page by two animation frames — the
 *  reported "whole site jumps up while still showing the old product". App.vue no
 *  longer wraps the page in a <Transition>, so the new DOM is already in place by
 *  the time this runs and the swap + scroll land in the same frame: one visual
 *  step, no rAF needed here. */
export function pageScroll(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  saved: { left: number; top: number } | null,
) {
  // Back / forward: put the shopper back where they were. The old unconditional
  // `{ top: 0 }` threw savedPosition away, so Back out of a product dumped you at
  // the top of a collection you had scrolled halfway down.
  if (saved) return saved
  if (to.hash) return { el: to.hash, top: 72 } // clear of the sticky header
  // NOT behavior:"smooth" — smooth-scrolling a long product page back to the top
  // is the "whole site slides" feeling from the report, just slower.
  return { top: 0 }
}

/** How long the OUTGOING page may stay on screen waiting for the next page's
 *  critical data. Past this we commit anyway and the incoming page shows its own
 *  skeleton — a tap must never feel dead.
 *
 *  Sized on measurement, not taste: the product endpoint answers in 3-9ms locally,
 *  so the real cost is the ~240-350ms Cloudflare edge→origin round trip (see
 *  docs/DEPLOY.md §7.4), i.e. ~300-400ms end to end. A budget below that would
 *  expire on most real navigations and buy nothing. */
export const HOLD_MS = 400

/** True while a navigation is being held. App.vue renders this as a slim top
 *  progress bar — the equivalent of Remix's `useNavigation().state === "loading"`.
 *  Without it the hold turns "jarring" into "the tap did nothing", which is worse. */
export const navPending = ref(false)

/** Route name → the CRITICAL read for that route: what Hydrogen would `await`.
 *  The page cannot render anything meaningful without it. */
const critical: Record<string, (to: RouteLocationNormalized) => [string, () => Promise<unknown>]> = {
  product: to => {
    const h = String(to.params.handle)
    return [qk.product(h), () => api.product(h)]
  },
}

/** Route name → the DEFERRED reads: started here so they travel on the same round
 *  trip, never awaited, and their space is reserved in markup. Hydrogen's `defer`,
 *  encoded as data. Awaiting these would delay the swap for content that is below
 *  the fold; not starting them at all is what made "You may also like" arrive as a
 *  second, separate height jump. */
const deferred: Record<string, (to: RouteLocationNormalized) => void> = {
  product: to => {
    const h = String(to.params.handle)
    void warmQuery(qk.related(h), () => api.related(h, 4)).catch(() => undefined)
  },
}

/** Route name → anything else the incoming page needs before it can render whole,
 *  waited for inside the same budget. Registered by the page's own module
 *  (lib/pdpblocks.ts for the product page), so none of it sits in the entry chunk.
 *  Returns undefined when there is nothing left to wait for. */
const extras: Record<string, () => Promise<void> | undefined> = {}

export function holdAlsoFor(name: string, wait: () => Promise<void> | undefined): void {
  extras[name] = wait
}

/* Generation counter. A third click supersedes the second, and only the NEWEST
   hold may clear navPending — otherwise the superseded hold's `finally` turns off
   the indicator for a navigation that is still running. */
let gen = 0

/** Hold the current page until `to`'s critical data is cached, or the budget runs
 *  out. Never throws and never hangs: both are worse than a skeleton. */
export async function holdRoute(to: RouteLocationNormalized): Promise<void> {
  const build = critical[String(to.name)]
  if (!build) return
  const [key, fetcher] = build(to)

  deferred[String(to.name)]?.(to)
  const extra = extras[String(to.name)]?.()

  // Prefetched on intent, or stale-but-present: zero hold. fetchkit returns a
  // stale value immediately under swr, so this is a true stale-while-revalidate
  // fast path, not merely a fresh-cache one.
  const warmAlready = peekQuery(key) !== undefined
  if (warmAlready && !extra) return

  const mine = ++gen
  navPending.value = true
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    // Swallow the rejection: a 404 must still NAVIGATE, so the page can render its
    // own "Product not found" state. A guard that rejects here would hang the click.
    const warm = warmAlready
      ? undefined
      : warmQuery(key, fetcher).then(
          () => undefined,
          () => undefined,
        )
    const budget = new Promise<void>(resolve => {
      timer = setTimeout(resolve, HOLD_MS)
    })
    await Promise.race([Promise.all([warm, extra]), budget])
  } finally {
    if (timer) clearTimeout(timer)
    if (mine === gen) navPending.value = false
  }
}

/** Test seam: reset the generation counter and indicator between cases. */
export function resetHoldForTests(): void {
  gen = 0
  navPending.value = false
  for (const k of Object.keys(extras)) delete extras[k]
}
